import {
  Environment as EnvName,
  isValidEnvironment,
} from '@celito.clients/types';

/**
 * WI-F2 — De-eagerized config split (ADO #38530).
 *
 * This module now holds ONLY genuinely BUILD-TIME values — things baked into
 * the bundle that are the same for every tenant: environment name, log levels,
 * Sentry / App Insights keys, date formats, timeouts, and the dormant Graph
 * presence scope.
 *
 * TENANT-SPECIFIC / RUNTIME auth values (clientId, authority, audience,
 * redirectUri, api scopes) are NO LONGER sourced here. They arrive at runtime
 * from `GET /api/v1/config` as a `TenantConfig` and are turned into an MSAL
 * config by the pure builders in `./auth-config.ts` — see `buildMsalConfig` /
 * `buildMsalScopes`. That is the "runtime layer populated from TenantConfig".
 *
 * IMPORTANT: the top-level `throw` on an invalid `VITE_ENV_NAME` was REMOVED.
 * Crashing at module load runs before any React error boundary or the
 * tenant-error boot screen can render, turning a bad env var into a blank
 * page. We now fall back to a safe default and let boot proceed.
 *
 * NOTE: later waves (WI-F5 network base + 401 redirect URLs, WI-F6 deep-link
 * base, WI-F7 Okta e-sign) still read some of the values below at build time.
 * They are intentionally kept available so those files keep compiling until
 * their own conversion lands. Do not remove them here.
 */

const DEFAULT_ENV_NAME: EnvName = EnvName.Production;

/**
 * Resolve the build-time environment name without throwing. An invalid or
 * absent `VITE_ENV_NAME` falls back to the safest default (Production log
 * levels / behavior) instead of crashing the app before error boundaries.
 */
export const resolveEnvName = (raw: unknown): EnvName =>
  isValidEnvironment(raw) ? raw : DEFAULT_ENV_NAME;

/**
 * Full build-time env map seeded into the `Environment` singleton.
 *
 * Retained (shape-compatible with the previous export) so the singleton keeps
 * serving the build-time values that not-yet-converted later-wave modules
 * (network / url util / e-sign) still read. The tenant-specific auth keys that
 * used to live here are gone — they now flow in at runtime via `TenantConfig`.
 */
export const environment = {
  NX_ENV_NAME: resolveEnvName(import.meta.env.VITE_ENV_NAME),
  NX_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  NX_REDIRECT_BASE_URL: import.meta.env.VITE_REDIRECT_BASE_URL,
  NX_NOT_INVITED_URL: import.meta.env.VITE_NOT_INVITED_URL,
  NX_NOT_ACTIVE_USER_URL: import.meta.env.VITE_NOT_ACTIVE_USER_URL,
  SUPERSET_BASE_URI: import.meta.env.VITE_SUPERSET_BASE_URI,
  SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
  SENTRY_RELEASE_NAME: import.meta.env.VITE_SENTRY_RELEASE_NAME,
  SENTRY_DIST: import.meta.env.VITE_SENTRY_DIST,
  MAX_LOG_LEVEL: import.meta.env.VITE_MAX_LOG_LEVEL,
  APPINSIGHTS_INSTRUMENTATIONKEY: import.meta.env
    .VITE_APPINSIGHTS_INSTRUMENTATIONKEY,
  APPINSIGHTS_CONNECTION_STRING: import.meta.env
    .VITE_APPINSIGHTS_CONNECTION_STRING,
  NX_WOPI_LOCK_REQUEST_INTERVAL_IN_MILLISECS: import.meta.env
    .VITE_WOPI_LOCK_REQUEST_INTERVAL_IN_MILLISECS,
  // Graph presence scope — dormant (no caller passes isGraphRequest=true), kept
  // build-time and injected into MSAL by `buildMsalScopes` (no feature work).
  NX_PRESENCE_SCOPE: import.meta.env.VITE_PRESENCE_SCOPE,
  // WI-F7 (later wave) still reads these at build time.
  OKTA_ISSUER: import.meta.env.VITE_OKTA_ISSUER,
  OKTA_CLIENT_ID: import.meta.env.VITE_OKTA_CLIENT_ID,
  OKTA_REDIRECT_URI: import.meta.env.VITE_OKTA_REDIRECT_URI,
  MAX_TIME_MINUTES_TIME_OUT: import.meta.env.VITE_MAX_TIME_MINUTES_TIME_OUT,
  SHOW_TIME_OUT_LOGS: import.meta.env.VITE_SHOW_TIME_OUT_LOGS,
};

export type BuildTimeEnvironment = typeof environment;
